/**
 * 学生管理 store 模块
 */
import {
  getStudents,
  getStudent,
  createStudent as apiCreate,
  updateStudent as apiUpdate,
  deleteStudent as apiDelete
} from '@/api/students'
import { normalizeGenderValue } from '@/utils/gender'
import { normalizeCommuterValue } from '@/utils/studentCommuter'

function normalizeStudent(raw) {
  if (!raw) return null
  const isCommuter = normalizeCommuterValue(raw.is_commuter ?? raw.isCommuter)
  const studentId = raw.student_id ?? raw.studentId ?? raw.id
  return {
    id: studentId,
    studentId,
    student_id: studentId,
    name: raw.student_name ?? raw.name ?? '',
    studentNo: raw.student_number ?? raw.studentNo ?? '',
    phone: raw.student_phone ?? raw.phone ?? '',
    gender: normalizeGenderValue(raw.gender ?? raw.sex),
    status: raw.status ?? '',
    classId: raw.class_id ?? raw.classId,
    className: raw.class_name ?? raw.className ?? '',
    userId: raw.user_id ?? raw.userId,
    card: raw.card ?? '',
    dingId: raw.ding_id ?? raw.dingId ?? '',
    isCommuter,
    is_commuter: isCommuter,
    photoFile: raw.photo_file ?? raw.photoFile ?? '',
    createBy: raw.create_by ?? raw.createBy,
    createTime: raw.create_time ?? raw.createTime,
    updateBy: raw.update_by ?? raw.updateBy,
    updateTime: raw.update_time ?? raw.updateTime
  }
}

function isFileLike(value) {
  return typeof File !== 'undefined' && value instanceof File
}

function normalizeOptionalId(value) {
  if (value === undefined || value === null || value === '') return undefined
  const text = String(value).trim()
  if (!text) return undefined
  return /^\d+$/.test(text) ? Number(text) : text
}

function resolveOperatorName(rootState = {}) {
  const userInfo = rootState.userInfo || {}
  const currentUser = rootState.currentUser || {}
  return String(
    userInfo.username ??
    userInfo.real_name ??
    userInfo.teacher_name ??
    userInfo.name ??
    currentUser.name ??
    'admin'
  ).trim() || 'admin'
}

function buildStudentPayload(source = {}) {
  const studentNo = (source.studentNo ?? source.student_number ?? '').toString().trim()
  const payload = {
    student_number: studentNo,
    class_id: normalizeOptionalId(source.classId ?? source.class_id),
    name: (source.name ?? '').toString().trim(),
    card: (source.card ?? '').toString().trim(),
    ding_id: (source.dingId ?? source.ding_id ?? '').toString().trim()
  }

  if (source.phone !== undefined || source.student_phone !== undefined) {
    payload.student_phone = (source.phone ?? source.student_phone ?? '').toString().trim()
  }

  const genderSource = source.gender ?? source.sex
  if (genderSource !== undefined && genderSource !== null && genderSource !== '') {
    payload.gender = normalizeGenderValue(genderSource)
  }

  if (source.user_id != null || source.userId != null) {
    payload.user_id = normalizeOptionalId(source.user_id ?? source.userId)
  }

  if (source.is_commuter !== undefined || source.isCommuter !== undefined) {
    payload.is_commuter = normalizeCommuterValue(source.is_commuter ?? source.isCommuter)
  }

  const photoValue = source.photo_file ?? source.photoFile
  if (photoValue != null && photoValue !== '') {
    payload.photo_file = photoValue
  }

  if (source.create_by != null || source.createBy != null) {
    payload.create_by = source.create_by ?? source.createBy
  }

  if (source.update_by != null || source.updateBy != null) {
    payload.update_by = source.update_by ?? source.updateBy
  }

  if (!isFileLike(payload.photo_file)) {
    if (typeof payload.photo_file === 'string') {
      payload.photo_file = payload.photo_file.trim()
      if (!payload.photo_file) delete payload.photo_file
    }
    return payload
  }

  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    formData.append(key, value)
  })
  return formData
}

export default {
  namespaced: true,
  state: {
    list: [],
    loading: false,
    error: null
  },
  getters: {
    students: state => state.list,
    loading: state => state.loading,
    error: state => state.error
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = (list || []).map(normalizeStudent).filter(Boolean)
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getStudents(params)
        const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
        const pagination = res.pagination ?? {}
        const statistics = res.statistics ?? {}
        const totalCount = Number(
          pagination.total_count
          ?? pagination.totalCount
          ?? res.total_count
          ?? res.totalCount
          ?? statistics.total_count
          ?? statistics.totalCount
          ?? res.count
          ?? list.length
        ) || 0
        const maleCount = Number(
          res.male_count
          ?? res.maleCount
          ?? statistics.male_count
          ?? statistics.maleCount
          ?? 0
        ) || 0
        const femaleCount = Number(
          res.female_count
          ?? res.femaleCount
          ?? statistics.female_count
          ?? statistics.femaleCount
          ?? 0
        ) || 0
        const onLeaveCount = Number(
          res.on_leave_count
          ?? res.onLeaveCount
          ?? statistics.on_leave_count
          ?? statistics.onLeaveCount
          ?? 0
        ) || 0
        if (process.env.NODE_ENV === 'development' && list.length) {
          console.log('[学生 store] API 原始返回第一条:', list[0], '所有字段:', Object.keys(list[0] || {}))
        }
        commit('SET_LIST', list)
        return {
          list: (list || []).map(normalizeStudent).filter(Boolean),
          totalCount,
          maleCount,
          femaleCount,
          onLeaveCount
        }
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchByClassId(_, classId) {
      return this.dispatch('students/fetchList', { class_id: classId })
    },
    async fetchOne(_, id) {
      const res = await getStudent(id)
      const data = res.data ?? res
      return normalizeStudent(data)
    },
    async create(_, payload) {
      await apiCreate(buildStudentPayload(payload))
    },
    async update({ rootState }, { id, data }) {
      const payload = { ...(data || {}) }
      if (payload.update_by == null && payload.updateBy == null) {
        payload.updateBy = resolveOperatorName(rootState)
      }
      await apiUpdate(id, buildStudentPayload(payload))
    },
    async remove(_, id) {
      await apiDelete(id)
    }
  }
}
